import {
  createCalculatorUserLayerV4ValidationBoundary,
} from "../calculator-user-layer-v4.js";
import {
  CALCULATOR_LAYER_CUSTOM_CATALOG_CHANGED_EVENT,
  CALCULATOR_LAYER_DOCUMENT_ACTIVATED_EVENT,
  CALCULATOR_LAYER_ITEM_SELECTED_EVENT,
  CALCULATOR_LAYER_INSPECTOR_TAG,
  CALCULATOR_LAYER_LOOT_SUBJECT_SELECTED_EVENT,
  CALCULATOR_LAYER_TRACE_SELECTED_EVENT,
} from "./calculator-layer-inspector.js";
import {
  calculatorInspectorUserLayerDocumentsEqual,
  parseCalculatorInspectorDataPresentationV1,
  parseCalculatorInspectorCustomCatalogItems,
} from "./calculator-layer-inspector-contract.js";
import {
  calculatorFishingSourcesEqual,
} from "./calculator-fishing-source.js";
import {
  DEFAULT_CALCULATOR_LOOT_SORT,
  parseCalculatorLootSort,
} from "./calculator-loot-sort.js";
import {
  parseCalculatorLootRateDisplayMode,
} from "./calculator-loot-rate-presentation.js";
import {
  parseCalculatorLootSubject,
} from "./calculator-loot-subject.js";
import {
  calculatorLootEditorFocusedControl,
  restoreCalculatorLootEditorFocusedControl,
} from "./calculator-loot-table-editor-interactions.js";
import {
  CALCULATOR_DATA_LANGUAGE_CHANGE_EVENT,
  calculatorDataLanguageTarget,
  calculatorDataText as t,
} from "./calculator-data-i18n.js";

export const CALCULATOR_LAYER_INSPECTOR_HOST_TAG =
  "fishy-calculator-layer-inspector-host";
export const CALCULATOR_LAYER_DOCUMENT_CONFIRMED_EVENT =
  "fishystuff:calculator-user-layer-confirmed";
export const CALCULATOR_LAYER_BOUNDARY_STATUS_EVENT =
  "fishystuff:calculator-layer-boundary-status";

const HTMLElementBase = globalThis.HTMLElement ?? class {};

function upgradeProperty(element, property) {
  if (!Object.prototype.hasOwnProperty.call(element, property)) {
    return;
  }
  const value = element[property];
  delete element[property];
  element[property] = value;
}

function escapeText(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function eventWithDetail(type, detail) {
  if (typeof globalThis.CustomEvent === "function") {
    return new globalThis.CustomEvent(type, {
      bubbles: true,
      composed: true,
      detail,
    });
  }
  const event = new Event(type, { bubbles: true });
  Object.defineProperty(event, "detail", { value: detail });
  return event;
}

function errorMessage(error) {
  return error instanceof Error ? error.message : String(error);
}

function statusFromError(error) {
  if (error?.response?.outcome === "superseded") {
    return "Local";
  }
  return error?.response?.outcome === "stale" ? "Stale" : "Failed";
}

function validationErrorDetail(error) {
  if (error?.response?.outcome === "superseded") {
    return message(
      "calculator.data.status.validation_superseded",
      "Newer local input superseded this validation response.",
    );
  }
  return errorMessage(error);
}

function message(key, fallback, variables = {}) {
  return Object.freeze({ key, fallback, variables: Object.freeze({ ...variables }) });
}

function translatedMessage(value) {
  return value && typeof value === "object"
    ? t(value.key, value.fallback, value.variables)
    : String(value ?? "");
}

function boundaryLabel(boundary) {
  const entry = {
    initialization: ["calculator.data.boundary.initialization", "initialization"],
  }[boundary];
  return entry ? t(entry[0], entry[1]) : String(boundary ?? "");
}

function engineStatusRenderKey(value) {
  const status = value?.status ?? null;
  if (status === "stale" || status === "failed") {
    return `warning:${String(value?.message ?? "")}`;
  }
  return "ready";
}

function engineStatusIsBusy(value) {
  return value?.status === "loading" || value?.status === "validating";
}

export class FishyCalculatorLayerInspectorHost extends HTMLElementBase {
  constructor() {
    super();
    this._model = null;
    this._projection = null;
    this._userLayerDocument = null;
    this._customCatalogItems = Object.freeze([]);
    this._catalogSearchUrl = "";
    this._rankingEvidenceQuery = "";
    this._selectedTraceId = null;
    this._selectedItemId = null;
    this._selectedLootSubject = null;
    this._discardInspectorViewStateOnRender = false;
    this._dataPresentation = null;
    this._lootSort = DEFAULT_CALCULATOR_LOOT_SORT;
    this._lootRateDisplayMode = "normalized";
    this._engineStatus = null;
    this._draftPreparer = null;
    this._documentValidator = null;
    this._boundary = null;
    this._languageTarget = null;
    this._status = "Local";
    this._statusDetail = message(
      "calculator.data.status.waiting_for_model",
      "Waiting for the calculator model.",
    );
    this._connected = false;
    this._initializationRequested = false;
    this._projectionSyncFrameId = 0;
    this._boundaryRenderRequested = true;
    this._paintResizeObserver = null;
    this._validationSequence = 0;
    this._handleActivatedBound = (event) => this._handleActivated(event);
    this._handleCustomCatalogChangedBound = (event) => (
      this._handleCustomCatalogChanged(event)
    );
    this._handleItemSelectedBound = (event) => this._handleItemSelected(event);
    this._handleTraceSelectedBound = (event) => this._handleTraceSelected(event);
    this._handleLootSubjectSelectedBound = (event) => (
      this._handleLootSubjectSelected(event)
    );
    this._handleLanguageChangeBound = () => this.refreshBoundary();
  }

  connectedCallback() {
    for (const property of [
      "model",
      "projection",
      "userLayerDocument",
      "customCatalogItems",
      "catalogSearchUrl",
      "dataPresentation",
      "lootSort",
      "lootRateDisplayMode",
      "engineStatus",
      "draftPreparer",
      "documentValidator",
    ]) {
      upgradeProperty(this, property);
    }
    this._connected = true;
    this._rebuildBoundary();
    this.addEventListener(
      CALCULATOR_LAYER_DOCUMENT_ACTIVATED_EVENT,
      this._handleActivatedBound,
    );
    this.addEventListener(
      CALCULATOR_LAYER_CUSTOM_CATALOG_CHANGED_EVENT,
      this._handleCustomCatalogChangedBound,
    );
    this.addEventListener(
      CALCULATOR_LAYER_ITEM_SELECTED_EVENT,
      this._handleItemSelectedBound,
    );
    this.addEventListener(
      CALCULATOR_LAYER_TRACE_SELECTED_EVENT,
      this._handleTraceSelectedBound,
    );
    this.addEventListener(
      CALCULATOR_LAYER_LOOT_SUBJECT_SELECTED_EVENT,
      this._handleLootSubjectSelectedBound,
    );
    this._languageTarget = calculatorDataLanguageTarget();
    this._languageTarget?.addEventListener?.(
      CALCULATOR_DATA_LANGUAGE_CHANGE_EVENT,
      this._handleLanguageChangeBound,
    );
    if (
      this._paintResizeObserver === null
      && typeof globalThis.ResizeObserver === "function"
    ) {
      this._paintResizeObserver = new globalThis.ResizeObserver(() => {
        this.refreshProjection();
      });
      this._paintResizeObserver.observe(this);
    }
    this._validateInitialDocumentIfReady();
    this.refreshBoundary();
  }

  disconnectedCallback() {
    this._connected = false;
    if (
      this._projectionSyncFrameId
      && typeof globalThis.cancelAnimationFrame === "function"
    ) {
      globalThis.cancelAnimationFrame(this._projectionSyncFrameId);
    }
    this._projectionSyncFrameId = 0;
    this._paintResizeObserver?.disconnect();
    this._paintResizeObserver = null;
    this._validationSequence += 1;
    this.removeEventListener(
      CALCULATOR_LAYER_DOCUMENT_ACTIVATED_EVENT,
      this._handleActivatedBound,
    );
    this.removeEventListener(
      CALCULATOR_LAYER_CUSTOM_CATALOG_CHANGED_EVENT,
      this._handleCustomCatalogChangedBound,
    );
    this.removeEventListener(
      CALCULATOR_LAYER_ITEM_SELECTED_EVENT,
      this._handleItemSelectedBound,
    );
    this.removeEventListener(
      CALCULATOR_LAYER_TRACE_SELECTED_EVENT,
      this._handleTraceSelectedBound,
    );
    this.removeEventListener(
      CALCULATOR_LAYER_LOOT_SUBJECT_SELECTED_EVENT,
      this._handleLootSubjectSelectedBound,
    );
    this._languageTarget?.removeEventListener?.(
      CALCULATOR_DATA_LANGUAGE_CHANGE_EVENT,
      this._handleLanguageChangeBound,
    );
    this._languageTarget = null;
  }

  get model() {
    return this._model;
  }

  set model(value) {
    const projection = value?.modelRevision === this._projection?.modelRevision
      ? this._projection
      : null;
    this.setEvaluationState(value, projection);
  }

  get projection() {
    return this._projection;
  }

  set projection(value) {
    if (value === this._projection) {
      return;
    }
    this._assertEvaluationState(this._model, value);
    this._projection = value;
    this.refreshProjection();
  }

  setEvaluationState(model, projection) {
    this._assertEvaluationState(model, projection);
    if (model === this._model && projection === this._projection) {
      return;
    }
    const modelChanged = model !== this._model;
    const modelContextChanged = model?.modelRevision !== this._model?.modelRevision;
    const fishingSourceChanged = !calculatorFishingSourcesEqual(
      model?.selection?.fishingSource ?? null,
      this._model?.selection?.fishingSource ?? null,
    );
    const preserveSelectedItem = Number.isInteger(this._selectedItemId)
      && model?.selection?.supplementalItemIds?.includes(this._selectedItemId);
    this._model = model;
    this._projection = projection;
    if (modelContextChanged) {
      this._validationSequence += 1;
      if (fishingSourceChanged) {
        this._customCatalogItems = Object.freeze([]);
      }
      this._rankingEvidenceQuery = "";
      this._selectedTraceId = null;
      this._selectedLootSubject = null;
      if (!preserveSelectedItem) {
        this._selectedItemId = null;
      }
      this._discardInspectorViewStateOnRender = true;
      if (this._dataPresentation?.modelRevision !== model?.modelRevision) {
        this._dataPresentation = null;
      }
    }
    if (modelChanged) {
      this._rebuildBoundary();
    }
    this.refreshProjection();
  }

  _assertEvaluationState(model, projection) {
    if (
      model !== null
      && projection !== null
      && model?.modelRevision !== projection?.modelRevision
    ) {
      throw new Error("calculator inspector model and projection revisions must match");
    }
  }

  /**
   * Coalesces the inspector's projection paint without rebuilding its boundary
   * controls. Hidden workspace sections retain the latest property and catch
   * up when the workspace layout next changes.
   */
  refreshProjection() {
    if (!this._connected || this._projectionSyncFrameId) {
      return;
    }
    if (typeof globalThis.requestAnimationFrame !== "function") {
      this._syncProjectionIfVisible();
      return;
    }
    this._projectionSyncFrameId = globalThis.requestAnimationFrame(() => {
      this._projectionSyncFrameId = 0;
      this._syncProjectionIfVisible();
    });
  }

  refreshBoundary() {
    this._boundaryRenderRequested = true;
    this.refreshProjection();
  }

  _syncProjectionIfVisible() {
    if (
      typeof this.getClientRects === "function"
      && this.getClientRects().length === 0
    ) {
      return false;
    }
    if (this._boundaryRenderRequested || this.innerHTML === "") {
      this._boundaryRenderRequested = false;
      this.render();
      return true;
    }
    let inspector = this.querySelector?.(CALCULATOR_LAYER_INSPECTOR_TAG);
    if (!inspector) {
      this.render();
      inspector = this.querySelector?.(CALCULATOR_LAYER_INSPECTOR_TAG);
      return Boolean(inspector);
    }
    this._syncInspector();
    return true;
  }

  get userLayerDocument() {
    return this._userLayerDocument;
  }

  set userLayerDocument(value) {
    if (calculatorInspectorUserLayerDocumentsEqual(
      value,
      this._userLayerDocument,
    )) {
      return;
    }
    this._userLayerDocument = value;
    this._status = value ? "Local" : this._status;
    this._statusDetail = value
      ? message(
        "calculator.data.status.active_local",
        "The active user layer is evaluated locally.",
      )
      : this._statusDetail;
    this.refreshProjection();
    this._validateInitialDocumentIfReady();
  }

  get customCatalogItems() {
    return this._customCatalogItems;
  }

  set customCatalogItems(value) {
    this._customCatalogItems = Object.freeze(
      parseCalculatorInspectorCustomCatalogItems(value),
    );
    this.refreshProjection();
  }

  get catalogSearchUrl() {
    return this._catalogSearchUrl;
  }

  set catalogSearchUrl(value) {
    if (typeof value !== "string") {
      throw new TypeError("catalogSearchUrl must be a string");
    }
    if (value === this._catalogSearchUrl) {
      return;
    }
    this._catalogSearchUrl = value;
    this._syncInspector();
  }

  get dataPresentation() {
    return this._dataPresentation;
  }

  get lootSort() {
    return this._lootSort;
  }

  set lootSort(value) {
    const parsed = parseCalculatorLootSort(value);
    if (parsed.by === this._lootSort.by && parsed.order === this._lootSort.order) {
      return;
    }
    this._lootSort = parsed;
    this._syncInspector();
  }

  get lootRateDisplayMode() {
    return this._lootRateDisplayMode;
  }

  set lootRateDisplayMode(value) {
    const mode = parseCalculatorLootRateDisplayMode(
      value,
      "lootRateDisplayMode",
    );
    if (mode === this._lootRateDisplayMode) {
      return;
    }
    this._lootRateDisplayMode = mode;
    this._syncInspector();
  }

  get engineStatus() {
    return this._engineStatus;
  }

  set engineStatus(value) {
    if (value !== null && (typeof value !== "object" || Array.isArray(value))) {
      throw new TypeError("engineStatus must be an object or null");
    }
    const status = value?.status ?? null;
    if (
      status !== null
      && !["loading", "local", "validating", "validated", "stale", "failed"]
        .includes(status)
    ) {
      throw new TypeError("engineStatus has an unsupported calculator status");
    }
    if (value === this._engineStatus) {
      return;
    }
    const presentationChanged = engineStatusRenderKey(value)
      !== engineStatusRenderKey(this._engineStatus);
    const modelLoadingChanged = engineStatusIsBusy(value)
      !== engineStatusIsBusy(this._engineStatus);
    this._engineStatus = value;
    // Only persistent warnings change the outer Data presentation. Busy
    // transitions update the nested editor's accessibility and edit-lock state
    // without rebuilding the complete WYSIWYG boundary.
    if (presentationChanged || !this._connected) {
      this.refreshBoundary();
    } else if (modelLoadingChanged) {
      this._syncInspector();
    }
  }

  set dataPresentation(value) {
    const parsed = parseCalculatorInspectorDataPresentationV1(value, this._model);
    if (parsed === this._dataPresentation) {
      return;
    }
    this._dataPresentation = parsed;
    this._syncInspector();
  }

  get draftPreparer() {
    return this._draftPreparer;
  }

  set draftPreparer(value) {
    if (value !== null && typeof value !== "function") {
      throw new TypeError("draftPreparer must be a function or null");
    }
    this._draftPreparer = value;
    this._syncInspector();
  }

  get documentValidator() {
    return this._documentValidator;
  }

  set documentValidator(value) {
    if (value !== null && typeof value !== "function") {
      throw new TypeError("documentValidator must be a function or null");
    }
    this._documentValidator = value;
    this._rebuildBoundary();
    this._validateInitialDocumentIfReady();
  }

  _rebuildBoundary() {
    this._boundary ??= createCalculatorUserLayerV4ValidationBoundary();
    this._boundary.setValidator(this._documentValidator);
    if (this._model?.modelRevision) {
      this._boundary.setModelRevision(this._model.modelRevision);
    }
  }

  _validateInitialDocumentIfReady() {
    if (
      !this._connected
      || this._initializationRequested
      || !this._userLayerDocument
      || typeof this._documentValidator !== "function"
    ) {
      return false;
    }
    void this._validateAndActivate(this._userLayerDocument, "initialization");
    return true;
  }

  _setStatus(status, detail, boundary = null, response = null, render = true) {
    this._status = status;
    this._statusDetail = detail;
    if (render) {
      this.refreshBoundary();
    }
    this.dispatchEvent(eventWithDetail(
      CALCULATOR_LAYER_BOUNDARY_STATUS_EVENT,
      Object.freeze({
        status,
        detail: translatedMessage(detail),
        boundary,
        modelRevision: this._model?.modelRevision ?? null,
        response,
      }),
    ));
  }

  async _validateAndActivate(document, boundary) {
    this._rebuildBoundary();
    if (boundary === "initialization") {
      this._initializationRequested = true;
    }
    const sequence = ++this._validationSequence;
    this._setStatus("Validating", message(
      "calculator.data.status.validating_boundary",
      "Validating user layer at the { $boundary } boundary.",
      { boundary: boundaryLabel(boundary) },
    ), boundary);
    try {
      const validated = await this._boundary.validate(document, boundary);
      if (sequence !== this._validationSequence || !this._connected) {
        return null;
      }
      this._userLayerDocument = validated.document;
      this._setStatus("Validated", message(
        "calculator.data.status.server_confirmed",
        "The server confirmed this user layer.",
      ), boundary, validated.response);
      this.dispatchEvent(eventWithDetail(
        CALCULATOR_LAYER_DOCUMENT_CONFIRMED_EVENT,
        Object.freeze({
          boundary,
          document: validated.document,
          response: validated.response,
        }),
      ));
      return validated;
    } catch (error) {
      if (sequence !== this._validationSequence || !this._connected) {
        return null;
      }
      this._setStatus(
        statusFromError(error),
        validationErrorDetail(error),
        boundary,
        error?.response ?? null,
      );
      return null;
    }
  }

  _handleActivated(event) {
    if (event.target === this) {
      return;
    }
    const inspector = event.target;
    this._rankingEvidenceQuery = inspector?.rankingEvidenceQuery
      ?? this._rankingEvidenceQuery;
    if (inspector?.selectedTraceId !== undefined) {
      this._selectedTraceId = inspector.selectedTraceId;
    }
    if (inspector?.selectedItemId !== undefined) {
      this._selectedItemId = inspector.selectedItemId;
    }
    if (inspector?.selectedLootSubject !== undefined) {
      this._selectedLootSubject = inspector.selectedLootSubject;
    }
    this._userLayerDocument = event.detail.document;
    this._status = "Local";
    this._statusDetail = message(
      "calculator.data.status.active_local",
      "The active user layer is evaluated locally.",
    );
  }

  _handleItemSelected(event) {
    if (
      event.target === this
      || event.detail?.modelRevision !== this._model?.modelRevision
    ) {
      return false;
    }
    if (event.detail?.itemId === null) {
      this._selectedItemId = null;
      return true;
    }
    const itemId = Number(event.detail?.itemId);
    if (!Number.isSafeInteger(itemId) || itemId <= 0) {
      return false;
    }
    this._selectedItemId = itemId;
    return true;
  }

  _handleTraceSelected(event) {
    if (event.target === this) {
      return false;
    }
    const traceId = event.detail?.traceId;
    if (
      typeof traceId !== "string"
      || !traceId
      || !(this._projection?.provenanceTraces ?? []).some((trace) => (
        trace.traceId === traceId
      ))
    ) {
      return false;
    }
    this._selectedTraceId = traceId;
    return true;
  }

  _handleLootSubjectSelected(event) {
    if (
      event.target === this
      || event.detail?.modelRevision !== this._model?.modelRevision
    ) {
      return false;
    }
    try {
      this._selectedLootSubject = parseCalculatorLootSubject(
        event.detail?.subject ?? null,
        "calculator loot subject selection",
      );
      return true;
    } catch {
      return false;
    }
  }

  _handleCustomCatalogChanged(event) {
    if (event.target === this) {
      return false;
    }
    this._customCatalogItems = Object.freeze(
      parseCalculatorInspectorCustomCatalogItems(event.detail?.items),
    );
    return true;
  }

  _syncInspector() {
    const inspector = this.querySelector?.(CALCULATOR_LAYER_INSPECTOR_TAG);
    if (!inspector) {
      return;
    }
    const state = {
      model: this._model,
      projection: this._projection,
      userLayerDocument: this._userLayerDocument,
      customCatalogItems: this._customCatalogItems,
      catalogSearchUrl: this._catalogSearchUrl,
      dataPresentation: this._dataPresentation,
      lootSort: this._lootSort,
      lootRateDisplayMode: this._lootRateDisplayMode,
      rankingEvidenceQuery: this._rankingEvidenceQuery,
      selectedTraceId: this._selectedTraceId,
      selectedItemId: this._selectedItemId,
      selectedLootSubject: this._selectedLootSubject,
      modelLoading: this._engineStatus?.status === "loading"
        || this._engineStatus?.status === "validating",
      draftPreparer: this._draftPreparer,
    };
    if (typeof inspector.setHostState === "function") {
      inspector.setHostState(state);
      return;
    }
    if (inspector.model !== this._model) {
      if (
        inspector.projection !== null
        && inspector.projection !== undefined
        && inspector.projection?.modelRevision !== this._model?.modelRevision
      ) {
        inspector.projection = null;
      }
      inspector.model = this._model;
    }
    if (inspector.projection !== this._projection) {
      inspector.projection = this._projection;
    }
    if (!calculatorInspectorUserLayerDocumentsEqual(
      inspector.userLayerDocument,
      this._userLayerDocument,
    )) {
      inspector.userLayerDocument = this._userLayerDocument;
    }
    inspector.customCatalogItems = this._customCatalogItems;
    inspector.catalogSearchUrl = this._catalogSearchUrl;
    inspector.dataPresentation = this._dataPresentation;
    inspector.lootSort = this._lootSort;
    inspector.lootRateDisplayMode = this._lootRateDisplayMode;
    inspector.rankingEvidenceQuery = this._rankingEvidenceQuery;
    inspector.selectedTraceId = this._selectedTraceId;
    inspector.selectedItemId = this._selectedItemId;
    inspector.selectedLootSubject = this._selectedLootSubject;
    inspector.modelLoading = state.modelLoading;
    inspector.draftPreparer = this._draftPreparer;
  }

  render() {
    if (!("innerHTML" in this)) {
      return;
    }
    const focused = calculatorLootEditorFocusedControl(this);
    const previousInspector = this.querySelector?.(CALCULATOR_LAYER_INSPECTOR_TAG);
    if (previousInspector && !this._discardInspectorViewStateOnRender) {
      this._rankingEvidenceQuery = previousInspector.rankingEvidenceQuery
        ?? this._rankingEvidenceQuery;
      if (previousInspector.selectedTraceId !== undefined) {
        this._selectedTraceId = previousInspector.selectedTraceId;
      }
      if (previousInspector.selectedItemId !== undefined) {
        this._selectedItemId = previousInspector.selectedItemId;
      }
      if (previousInspector.selectedLootSubject !== undefined) {
        this._selectedLootSubject = previousInspector.selectedLootSubject;
      }
    }
    this._discardInspectorViewStateOnRender = false;
    const boundaryStatus = this._status === "Validating"
      ? '<div class="alert alert-info" role="status" data-layer-boundary-alert>'
        + `<span>${escapeText(translatedMessage(this._statusDetail))}</span></div>`
      : this._status === "Failed" || this._status === "Stale"
        ? '<div class="alert alert-warning" role="alert" data-layer-boundary-alert>'
          + `<span>${escapeText(translatedMessage(this._statusDetail))}</span></div>`
        : "";
    const engineStatus = this._engineStatus?.status === "stale"
      || this._engineStatus?.status === "failed"
      ? '<div class="alert alert-warning" role="alert" data-layer-engine-status>'
        + `<span>${escapeText(this._engineStatus.message ?? t("calculator.data.status.model_refresh_failed", "Calculator model refresh failed."))}</span></div>`
      : "";
    this.innerHTML = `<div class="grid gap-4" data-layer-inspector-host>`
      + boundaryStatus
      + engineStatus
      + `<${CALCULATOR_LAYER_INSPECTOR_TAG}></${CALCULATOR_LAYER_INSPECTOR_TAG}>`
      + '</div>';
    this._syncInspector();
    restoreCalculatorLootEditorFocusedControl(this, focused);
  }
}

export function registerCalculatorLayerInspectorHost() {
  if (
    typeof globalThis.customElements !== "undefined"
    && !globalThis.customElements.get(CALCULATOR_LAYER_INSPECTOR_HOST_TAG)
  ) {
    globalThis.customElements.define(
      CALCULATOR_LAYER_INSPECTOR_HOST_TAG,
      FishyCalculatorLayerInspectorHost,
    );
  }
}
